import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import img from './icon/heroImgBackground.png';

class Particle {
  constructor(network) {
    this.canvas = network.canvas;
    this.g = network.g;
    this.particleColor = network.particleColor;
    this.x = Math.random() * this.canvas.width;
    this.y = Math.random() * this.canvas.height;
    this.velocity = {
      x: (Math.random() - 0.5) * network.velocity,
      y: (Math.random() - 0.5) * network.velocity
    };
  }

  update() {
    if (this.x > this.canvas.width + 20 || this.x < -20) {
      this.velocity.x = -this.velocity.x;
    }
    if (this.y > this.canvas.height + 20 || this.y < -20) {
      this.velocity.y = -this.velocity.y;
    }
    this.x += this.velocity.x;
    this.y += this.velocity.y;
  }

  draw() {
    this.g.beginPath();
    this.g.fillStyle = this.particleColor;
    this.g.globalAlpha = 0.7;
    this.g.arc(this.x, this.y, 1.5, 0, 2 * Math.PI);
    this.g.fill();
  }
}

class ParticleNetwork {
  constructor(canvas, g, options, container) {
    this.canvas = canvas;
    this.g = g;
    this.particleColor = options.particleColor;
    this.velocity = this.setVelocity(options.speed);
    this.density = this.setDensity(options.density);
    this.interactive = options.interactive;
    this.particles = [];
    this.mouseParticle = null;
    this.background = options.background;
    this.container = container;
    this.animationFrame = null;
  }

  setVelocity(speed) {
    if (speed === 'fast') return 1;
    if (speed === 'slow') return 0.33;
    if (speed === 'none') return 0;
    return 0.66; // medium
  }

  setDensity(density) {
    if (density === 'high') return 5000;
    if (density === 'low') return 20000;
    return isNaN(parseInt(density, 10)) ? 10000 : density;
  }

  init() {
    this.canvas.width = this.container.offsetWidth;
    this.canvas.height = this.container.offsetHeight;
    this.canvas.style.width = `${this.canvas.width}px`;
    this.canvas.style.height = `${this.canvas.height}px`;

    this.particles = [];
    const maxParticles = 250;
    const particleCount = Math.min((this.canvas.width * this.canvas.height) / this.density, maxParticles);

    for (let i = 0; i < particleCount; i++) {
      this.particles.push(new Particle(this));
    }

    if (this.interactive) {
      this.mouseParticle = new Particle(this);
      this.mouseParticle.velocity = { x: 0, y: 0 };
      this.particles.push(this.mouseParticle);

      const handleMouseMove = (e) => {
        const rect = this.canvas.getBoundingClientRect();
        this.mouseParticle.x = e.clientX - rect.left;
        this.mouseParticle.y = e.clientY - rect.top;
      };

      const handleMouseUp = () => {
        this.mouseParticle.velocity = {
          x: (Math.random() - 0.5) * this.velocity,
          y: (Math.random() - 0.5) * this.velocity
        };
        this.mouseParticle = new Particle(this);
        this.mouseParticle.velocity = { x: 0, y: 0 };
        this.particles.push(this.mouseParticle);
      };

      this.container.addEventListener('mousemove', handleMouseMove);
      this.container.addEventListener('mouseup', handleMouseUp);

      this.container._mouseMoveListener = handleMouseMove;
      this.container._mouseUpListener = handleMouseUp;
    }

    this.animate();
  }

  animate = () => {
    this.animationFrame = requestAnimationFrame(this.animate);
    this.g.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.g.globalAlpha = 1;

    for (let i = 0; i < this.particles.length; i++) {
      this.particles[i].update();
      this.particles[i].draw();

      for (let j = i + 1; j < this.particles.length; j++) {
        const dx = this.particles[i].x - this.particles[j].x;
        const dy = this.particles[i].y - this.particles[j].y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance <= 120) {
          this.g.beginPath();
          this.g.strokeStyle = this.particleColor;
          this.g.globalAlpha = (120 - distance) / 120;
          this.g.lineWidth = 0.7;
          this.g.moveTo(this.particles[i].x, this.particles[i].y);
          this.g.lineTo(this.particles[j].x, this.particles[j].y);
          this.g.stroke();
        }
      }
    }
  };

  resize(width, height) {
    this.canvas.width = width;
    this.canvas.height = height;
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;

    this.particles.forEach(p => {
      p.x = Math.random() * width;
      p.y = Math.random() * height;
    });
  }

  cleanup() {
    if (this.container._mouseMoveListener) {
      this.container.removeEventListener('mousemove', this.container._mouseMoveListener);
    }
    if (this.container._mouseUpListener) {
      this.container.removeEventListener('mouseup', this.container._mouseUpListener);
    }
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
  }
}

const ParticleBackground = ({
  particleColor = 'rgba(255, 255, 255, 0.63)',
  background = img,
  interactive = true,
  speed = 'medium',
  density = 'high'
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const particleNetworkRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    const ctx = canvas.getContext('2d');

    particleNetworkRef.current = new ParticleNetwork(canvas, ctx, {
      particleColor,
      background,
      interactive,
      speed,
      density
    }, container);
    particleNetworkRef.current.init();

    const handleResize = () => {
      const width = container.offsetWidth;
      const height = container.offsetHeight;
      particleNetworkRef.current.resize(width, height);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      particleNetworkRef.current.cleanup();
    };
  }, [particleColor, background, interactive, speed, density]);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        background: `url("${background}") no-repeat center`,
        backgroundSize: 'cover',
        overflow: 'hidden',
        filter: 'brightness(1.1)',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1,
          pointerEvents: 'auto',
        }}
      />
    </div>
  );
};

ParticleBackground.propTypes = {
  particleColor: PropTypes.string,
  background: PropTypes.string,
  interactive: PropTypes.bool,
  speed: PropTypes.oneOf(['fast', 'medium', 'slow', 'none']),
  density: PropTypes.oneOfType([
    PropTypes.oneOf(['high', 'low']),
    PropTypes.number
  ])
};

export default ParticleBackground;
